import { getDefaultProvider, type ProviderType } from '@/ai/providers';
import { getLanguageName } from './language-config';
import { getRecommendedModel } from '@/ai/models';
import { generateAIText } from './ai-text';

export interface TranslationResult {
  title: string;
  content: string;
}

/**
 * Translates a policy title and content to the target language using AI API
 * @param title - The policy title to translate
 * @param content - The policy content to translate (may include markdown)
 * @param targetLanguage - The target language code (e.g., 'ar' for Arabic)
 * @param providerType - Optional provider type ('openai' or 'gemini'), defaults to user preference
 * @returns Promise with translated title and content
 */
export async function translatePolicy(
  title: string,
  content: string,
  targetLanguage: string,
  providerType?: ProviderType
): Promise<TranslationResult> {
  // Validate inputs
  if (!title || !content) {
    throw new Error('Title and content are required for translation');
  }

  if (!targetLanguage) {
    throw new Error('Target language is required');
  }

  const languageName = getLanguageName(targetLanguage);

  // Use provided provider or default
  const provider = providerType || getDefaultProvider();
  const model = getRecommendedModel(provider, 'TRANSLATION');

  // Translate title and content separately for better quality
  const [translatedTitle, translatedContent] = await Promise.all([
    translateTitle(title, targetLanguage, languageName, provider, model),
    translateContent(content, targetLanguage, languageName, provider, model),
  ]);

  return {
    title: translatedTitle,
    content: translatedContent,
  };
}

/**
 * Translates the policy title
 */
async function translateTitle(
  title: string,
  targetLanguage: string,
  languageName: string,
  provider: ProviderType,
  model: string
): Promise<string> {
  const prompt = `You are a professional translator specializing in corporate policy documents. Translate the following policy title to ${languageName}. 

Policy Title: ${title}

Requirements:
- Maintain the professional tone and meaning
- Keep it concise and clear
- Do not add any explanations or notes
- Return only the translated title

Translated title:`;

  const translatedTitle = await generateAIText({
    provider,
    model,
    messages: [
      {
        role: 'user',
        content: prompt,
      },
    ],
    temperature: 0.2,
    maxTokens: 200,
  });
  
  if (!translatedTitle || translatedTitle.trim().length === 0) {
    throw new Error('Translation failed: Empty title returned');
  }

  return translatedTitle.trim();
}

/**
 * Translates the policy content, preserving markdown formatting
 */
async function translateContent(
  content: string,
  targetLanguage: string,
  languageName: string,
  provider: ProviderType,
  model: string
): Promise<string> {
  const prompt = `You are a professional translator specializing in corporate policy documents. Translate the following policy content to ${languageName}.

Policy Content:
${content}

Requirements:
- Maintain the exact same markdown formatting (headers, lists, bold, italic, etc.)
- Preserve all structural elements
- Keep the professional tone and legal accuracy
- Translate all text content while keeping markdown syntax intact
- Do not add any explanations, notes, or comments
- Return only the translated content with preserved formatting

Translated content:`;

  const translatedContent = await generateAIText({
    provider,
    model,
    messages: [
      {
        role: 'user',
        content: prompt,
      },
    ],
    temperature: 0.2,
    maxTokens: 4000,
  });
  
  if (!translatedContent || translatedContent.trim().length === 0) {
    throw new Error('Translation failed: Empty content returned');
  }

  // Basic validation - ensure we got meaningful content
  if (translatedContent.trim().length < content.length * 0.3) {
    throw new Error('Translation failed: Translated content seems too short');
  }

  return translatedContent.trim();
}

